import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/Logo';
import { Mail, ArrowLeft } from 'lucide-react';

/**
 * Step 1 of the reset flow — user enters their email; backend sends the OTP.
 * The response is deliberately generic ("If an account exists…") to avoid
 * leaking which addresses are registered. We nav forward regardless so the
 * user always ends up at the OTP screen with sessionStorage.reset_email set.
 */
export default function ForgotPasswordPage() {
  const { meta } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      sessionStorage.setItem('32d.reset.email', email.trim().toLowerCase());
      setSent(true);
      // brief delay so the "sent" banner is visible before nav
      setTimeout(() => nav('/verify-otp'), 900);
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r"
           style={{ background: 'hsl(var(--surface-1))', borderColor: 'hsl(var(--border))' }}>
        <Logo variant="horizontal" surface="light" height={44} testId="forgot-brand-logo" className="max-w-[280px]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Reset your password.
          </h1>
          <p className="mt-4 text-[hsl(var(--foreground-muted))] max-w-md text-[15px] leading-relaxed">
            Enter your work email and we&rsquo;ll send a 6-digit code that unlocks a new password.
            Codes expire in 10 minutes.
          </p>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-subtle))]">
          Reset codes go to your <span className="font-mono">@{meta?.allowed_email_domain || '32december.design'}</span> inbox.
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="forgot-password-form">
          <div className="lg:hidden flex justify-center mb-2">
            <Logo variant="stacked" surface="light" height={90} />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Mail size={22} />Forgot password
            </h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))]">
              We&rsquo;ll email you a 6-digit reset code.
            </p>
          </div>

          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Work email</span>
            <Input data-testid="forgot-email"
                   required type="email" value={email}
                   onChange={e => setEmail(e.target.value)}
                   placeholder={`you@${meta?.allowed_email_domain || '32december.design'}`}
                   className="mt-1" autoComplete="email" />
          </label>

          {error && (
            <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border"
                 style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
                 role="alert" data-testid="forgot-error">
              {error}
            </div>
          )}
          {sent && (
            <div className="text-[12.5px] text-[hsl(var(--success))] p-2 rounded border"
                 style={{ borderColor: 'hsl(var(--success))', background: 'hsl(142 70% 96%)' }}
                 data-testid="forgot-sent">
              If an account exists for this email, a reset code has been sent.
            </div>
          )}

          <Button data-testid="forgot-submit" disabled={busy || sent} type="submit" className="w-full">
            {busy ? 'Sending…' : sent ? 'Sent — redirecting…' : 'Send reset code'}
          </Button>

          <div className="text-[12.5px] text-center text-[hsl(var(--foreground-muted))]">
            <Link className="inline-flex items-center gap-1 underline" to="/login" data-testid="forgot-back-to-login">
              <ArrowLeft size={13} />Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
